<template>
  <div>
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <div class="content-header-title">
          <h2 class="content-title">Editar Contacto</h2>
          <p class="content-subtitle">Modifica la información del contacto</p>
        </div>
      </div>
      <div class="content-header-right">
        <b-button
          variant="outline-secondary"
          @click="$router.push({ name: 'contactos-lista' })"
        >
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          Volver a la Lista
        </b-button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !contacto" class="text-center py-5">
      <b-spinner class="mr-2"></b-spinner>
      <span>Cargando contacto...</span>
    </div>

    <!-- Form -->
    <b-card v-else>
      <b-form @submit.prevent="actualizarContacto">
        <b-row>
          <!-- Información Personal -->
          <b-col md="6">
            <h5 class="mb-3">Información Personal</h5>
            
            <b-form-group
              label="Nombre *"
              label-for="nombre"
              :invalid-feedback="errors.nombre"
              :state="errors.nombre ? false : null"
            >
              <b-form-input
                id="nombre"
                v-model="form.nombre"
                placeholder="Nombre completo del contacto"
                :state="errors.nombre ? false : null"
                required
              />
            </b-form-group>

            <b-form-group
              label="Cargo"
              label-for="cargo"
              :invalid-feedback="errors.cargo"
              :state="errors.cargo ? false : null"
            >
              <b-form-input
                id="cargo"
                v-model="form.cargo"
                placeholder="Cargo o posición en la empresa"
                :state="errors.cargo ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="Teléfono"
              label-for="telefono"
              :invalid-feedback="errors.telefono"
              :state="errors.telefono ? false : null"
            >
              <b-form-input
                id="telefono"
                v-model="form.telefono"
                placeholder="Número de teléfono"
                :state="errors.telefono ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="Email"
              label-for="email"
              :invalid-feedback="errors.email"
              :state="errors.email ? false : null"
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                placeholder="Correo electrónico"
                :state="errors.email ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="LinkedIn"
              label-for="linkedin"
              :invalid-feedback="errors.linkedin"
              :state="errors.linkedin ? false : null"
            >
              <b-form-input
                id="linkedin"
                v-model="form.linkedin"
                placeholder="URL del perfil de LinkedIn"
                :state="errors.linkedin ? false : null"
              />
            </b-form-group>
          </b-col>

          <!-- Información de Empresa -->
          <b-col md="6">
            <h5 class="mb-3">Información de Empresa</h5>
            
            <b-form-group
              label="Empresa *"
              label-for="empresa"
              :invalid-feedback="errors.empresa"
              :state="errors.empresa ? false : null"
            >
              <div class="d-flex gap-2">
                <v-select
                  v-model="form.empresa"
                  :options="empresasOptions"
                  placeholder="Buscar y seleccionar empresa..."
                  :state="errors.empresa ? false : null"
                  :filterable="true"
                  :searchable="true"
                  :clearable="true"
                  :loading="loadingEmpresas"
                  label="nombre"
                  :reduce="empresa => empresa.idEmpresa"
                  @search="buscarEmpresas"
                  required
                  class="flex-grow-1"
                >
                <template #no-options>
                  <div class="text-center py-2">
                    <span v-if="loadingEmpresas">Cargando empresas...</span>
                    <span v-else>No se encontraron empresas</span>
                  </div>
                </template>
                <template #option="{ nombre, direccion, telefono }">
                  <div>
                    <div class="font-weight-bold">{{ nombre }}</div>
                    <small class="text-muted" v-if="direccion">{{ direccion }}</small>
                    <small class="text-muted" v-if="telefono">{{ telefono }}</small>
                  </div>
                </template>
                  <template #selected-option="{ nombre }">
                    <span>{{ nombre }}</span>
                  </template>
                </v-select>
                <b-button 
                  variant="outline-primary" 
                  size="sm" 
                  @click="abrirModalEmpresa"
                  title="Crear nueva empresa"
                >
                  <feather-icon icon="PlusIcon" size="16" />
                </b-button>
              </div>
            </b-form-group>

            <b-form-group
              label="Observaciones"
              label-for="observaciones"
              :invalid-feedback="errors.observaciones"
              :state="errors.observaciones ? false : null"
            >
              <b-form-textarea
                id="observaciones"
                v-model="form.observaciones"
                placeholder="Observaciones adicionales sobre el contacto"
                rows="4"
                :state="errors.observaciones ? false : null"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Form Actions -->
        <hr class="my-4">
        <div class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="outline-secondary"
            class="mr-2"
            @click="$router.push({ name: 'contactos-lista' })"
          >
            Cancelar
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="loading"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-1"
            />
            <span v-if="loading">Actualizando...</span>
            <span v-else>Actualizar Contacto</span>
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Modal Nueva Empresa -->
    <b-modal id="modal-nueva-empresa" title="Nueva Empresa" @hidden="resetNuevaEmpresa" hide-footer>
      <b-form @submit.prevent="crearEmpresa">
        <b-form-group
          label="Nombre de la empresa *"
          label-for="empresa-nombre"
          :invalid-feedback="errorsEmpresa.nombre"
          :state="errorsEmpresa.nombre ? false : null"
        >
          <b-form-input
            id="empresa-nombre"
            v-model="nuevaEmpresa.nombre"
            placeholder="Introduce el nombre"
            :state="errorsEmpresa.nombre ? false : null"
            required
          />
        </b-form-group>

        <b-form-group label="CIF" label-for="empresa-cif" :invalid-feedback="errorsEmpresa.cif" :state="errorsEmpresa.cif ? false : null">
          <b-form-input id="empresa-cif" v-model="nuevaEmpresa.cif" placeholder="CIF (opcional)" :state="errorsEmpresa.cif ? false : null" />
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button type="button" variant="outline-secondary" class="mr-2" :disabled="creandoEmpresa" @click="cerrarModalEmpresa">Cancelar</b-button>
          <b-button type="submit" variant="primary" :disabled="creandoEmpresa">
            <b-spinner v-if="creandoEmpresa" small class="mr-1" />
            <span v-if="creandoEmpresa">Creando...</span>
            <span v-else>Crear</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { ref, onMounted, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { EmpresaService } from '@/services/empresaService'
import { ContactoService } from '@/services/contactoService'

export default {
  name: 'ContactosEditar',
  components: {
    vSelect,
  },
  setup(props, { root }) {
    // Reactive data
    const loading = ref(false)
    const loadingEmpresas = ref(false)
    const contacto = ref(null)
    const empresasOptions = ref([])
    const empresasCargadas = ref(false)
    // Modal Nueva Empresa
    const creandoEmpresa = ref(false)
    const nuevaEmpresa = ref({ nombre: '', cif: '' })
    const errorsEmpresa = ref({})
    // Form data
    const form = ref({
      nombre: '',
      cargo: '',
      telefono: '',
      email: '',
      linkedin: '',
      observaciones: '',
      empresa: null,
    })

    // Validation errors
    const errors = ref({})

    // Get contact ID from route
    const contactoId = root.$route.params.id

    // Methods
    const cargarContacto = async () => {
      loading.value = true
      try {
        console.debug('[ContactosEditar] cargarContacto()', { contactoId })
        contacto.value = await ContactoService.getById(parseInt(contactoId))

        // Llenar el formulario con los datos del contacto
        form.value = {
          nombre: contacto.value.nombre,
          cargo: contacto.value.cargo || '',
          telefono: contacto.value.telefono || '',
          email: contacto.value.email || '',
          linkedin: contacto.value.linkedin || '',
          observaciones: contacto.value.observaciones || '',
          empresa: contacto.value.empresa ? contacto.value.empresa.idEmpresa : contacto.value.idEmpresa || null,
        }
      } catch (error) {
        console.error('Error al cargar contacto:', error)
        // root.$toast.error('Error al cargar el contacto')
        // root.$router.push({ name: 'contactos-lista' })
      } finally {
        loading.value = false
      }
    }

    const cargarEmpresas = async (termino = '') => {
      if (loadingEmpresas.value) return
      
      loadingEmpresas.value = true
      try {
        console.debug('[ContactosEditar] cargarEmpresas()', { termino })
        if (termino && termino.trim().length >= 2) {
          console.debug('[ContactosEditar] Buscando empresas por nombre (>=2 chars)')
          empresasOptions.value = await EmpresaService.searchByName(termino.trim())
        } else {
          console.debug('[ContactosEditar] Cargando todas las empresas')
          empresasOptions.value = await EmpresaService.getAll()
        }
        console.debug('[ContactosEditar] Empresas recibidas:', empresasOptions.value?.length || 0)
        empresasCargadas.value = true
      } catch (error) {
        console.error('Error al cargar empresas:', error)
        // root.$toast.error('Error al cargar las empresas')
      } finally {
        loadingEmpresas.value = false
      }
    }

    const buscarEmpresas = (termino) => {
      console.debug('[ContactosEditar] buscarEmpresas()', { termino })
      if (termino && termino.length >= 2) {
        cargarEmpresas(termino)
      } else if (termino.length === 0) {
        cargarEmpresas()
      }
    }

    const abrirModalEmpresa = () => {
      resetNuevaEmpresa()
      root.$bvModal.show('modal-nueva-empresa')
    }
    const cerrarModalEmpresa = () => {
      root.$bvModal.hide('modal-nueva-empresa')
    }
    const resetNuevaEmpresa = () => {
      nuevaEmpresa.value = { nombre: '', cif: '' }
      errorsEmpresa.value = {}
      creandoEmpresa.value = false
    }
    const validarNuevaEmpresa = () => {
      errorsEmpresa.value = {}
      let ok = true
      if (!nuevaEmpresa.value.nombre || nuevaEmpresa.value.nombre.trim() === '') {
        errorsEmpresa.value.nombre = 'El nombre es obligatorio'
        ok = false
      } else if (nuevaEmpresa.value.nombre.length > 300) {
        errorsEmpresa.value.nombre = 'El nombre no puede exceder 300 caracteres'
        ok = false
      }
      if (nuevaEmpresa.value.cif && nuevaEmpresa.value.cif.length > 50) {
        errorsEmpresa.value.cif = 'El CIF no puede exceder 50 caracteres'
        ok = false
      }
      return ok
    }
    const crearEmpresa = async () => {
      if (!validarNuevaEmpresa()) return
      creandoEmpresa.value = true
      try {
        const payload = {
          nombre: nuevaEmpresa.value.nombre.trim(),
          direccion: '',
          idTipoEmpresa: null,
          cif: (nuevaEmpresa.value.cif || '').trim(),
          web: null,
          telefono: null,
          email: null,
          observaciones: null,
        }
          const empresaCreada = await EmpresaService.create(payload)
          empresasOptions.value = [empresaCreada, ...(empresasOptions.value || [])]
          form.value.empresa = empresaCreada.idEmpresa
          cerrarModalEmpresa()
      } catch (error) {
        console.error('Error al crear empresa:', error)
        const status = error && error.response && error.response.status
        const backendMsg = error && error.response && (error.response.data && (error.response.data.message || error.response.data.error || error.response.data))
        if (status === 409 || (typeof backendMsg === 'string' && backendMsg.toLowerCase().includes('ya existe'))) {
          if (root.$toast) root.$toast.error('Ya existe una empresa con ese CIF')
        } else if (root.$toast) {
          root.$toast.error('No se pudo crear la empresa')
        }
      } finally {
        creandoEmpresa.value = false
      }
    }

    const validarFormulario = () => {
      errors.value = {}
      let esValido = true

      // Validar nombre
      if (!form.value.nombre || form.value.nombre.trim() === '') {
        errors.value.nombre = 'El nombre es obligatorio'
        esValido = false
      } else if (form.value.nombre.length > 300) {
        errors.value.nombre = 'El nombre no puede exceder 300 caracteres'
        esValido = false
      }

      // Validar cargo
      if (form.value.cargo && form.value.cargo.length > 100) {
        errors.value.cargo = 'El cargo no puede exceder 100 caracteres'
        esValido = false
      }

      // Validar teléfono
      if (form.value.telefono && form.value.telefono.length > 100) {
        errors.value.telefono = 'El teléfono no puede exceder 100 caracteres'
        esValido = false
      }

      // Validar email
      if (form.value.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(form.value.email)) {
          errors.value.email = 'El formato del email no es válido'
          esValido = false
        } else if (form.value.email.length > 100) {
          errors.value.email = 'El email no puede exceder 100 caracteres'
          esValido = false
        }
      }

      // Validar LinkedIn
      if (form.value.linkedin && form.value.linkedin.length > 500) {
        errors.value.linkedin = 'El LinkedIn no puede exceder 500 caracteres'
        esValido = false
      }

      // Validar observaciones
      if (form.value.observaciones && form.value.observaciones.length > 2000) {
        errors.value.observaciones = 'Las observaciones no pueden exceder 2000 caracteres'
        esValido = false
      }

      // Validar empresa
      if (!form.value.empresa) {
        errors.value.empresa = 'La empresa es obligatoria'
        esValido = false
      } else {
        // Limpiar error de empresa si está seleccionada
        delete errors.value.empresa
      }

      return esValido
    }

    const actualizarContacto = async () => {
      if (!validarFormulario()) {
        // root.$toast.error('Por favor, corrige los errores en el formulario')
        console.log('Errores de validación:', errors.value)
        return
      }

      loading.value = true
      try {
        // Preparar datos para enviar
        const contactoData = {
          nombre: form.value.nombre.trim(),
          cargo: form.value.cargo.trim() || null,
          telefono: form.value.telefono.trim() || null,
          email: form.value.email.trim() || null,
          linkedin: form.value.linkedin.trim() || null,
          observaciones: form.value.observaciones.trim() || null,
          idEmpresa: form.value.empresa,
        }

        const actualizado = await ContactoService.update(parseInt(contactoId), contactoData)
        console.log('Contacto actualizado:', actualizado)
        // root.$toast.success('Contacto actualizado correctamente')
        root.$router.push({ name: 'contactos-lista' })
      } catch (error) {
        console.error('Error al actualizar contacto:', error)
        // root.$toast.error('Error al actualizar el contacto')
      } finally {
        loading.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      cargarContacto()
      cargarEmpresas()
    })

    return {
      loading,
      loadingEmpresas,
      contacto,
      empresasOptions,
      form,
      errors,
      cargarEmpresas,
      buscarEmpresas,
      actualizarContacto,
      // Modal Nueva Empresa
      creandoEmpresa,
      nuevaEmpresa,
      errorsEmpresa,
      abrirModalEmpresa,
      cerrarModalEmpresa,
      crearEmpresa,
    }
  },
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.content-header-title h2 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.content-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
